import React, { useEffect, useState } from "react";
import { CommentsAPI } from "@/lib/api";
import { useAuth } from "@/lib/providers";

export default function Comments({ assessmentId, dimensionKey, compact }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await CommentsAPI.list(assessmentId);
      setItems(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assessmentId]);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true); setErr("");
    try {
      await CommentsAPI.add(assessmentId, body.trim(), dimensionKey || null);
      setBody("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    await CommentsAPI.remove(assessmentId, cid);
    setItems((xs) => xs.filter((x) => x.comment_id !== cid));
  };

  const filtered = dimensionKey
    ? items.filter((c) => c.dimension_key === dimensionKey)
    : items.filter((c) => !c.dimension_key);

  return (
    <div data-testid={`comments-${dimensionKey || "general"}`}>
      {!compact && <div className="overline" style={{ marginBottom: 8 }}>Comments · {filtered.length}</div>}

      <div style={{ display: "grid", gap: 10 }}>
        {filtered.map((c) => (
          <div key={c.comment_id} className="v-surface-flat" data-testid={`comment-${c.comment_id}`} style={{ padding: 12, border: "1px solid var(--v-border)", borderRadius: "var(--radius)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div className="mono" style={{ fontSize: 11, color: "var(--v-text-2)" }}>
                <span style={{ color: "var(--v-text)" }}>{c.user_name}</span> · {new Date(c.created_at).toLocaleString()}
              </div>
              {(c.user_id === user?.user_id || user?.role === "admin") && (
                <button
                  onClick={() => remove(c.comment_id)}
                  data-testid={`comment-delete-${c.comment_id}`}
                  style={{ background: "transparent", border: "none", color: "var(--v-text-3)", cursor: "pointer", fontSize: 11 }}
                >
                  delete
                </button>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{c.body}</div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="mono" style={{ fontSize: 11, color: "var(--v-text-3)" }}>{compact ? "No notes yet." : "No comments yet. Start the discussion."}</div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <textarea
          className="v-textarea"
          placeholder={dimensionKey ? "Add a note for this dimension…" : "Add a comment…"}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid={`comment-input-${dimensionKey || "general"}`}
          style={{ minHeight: compact ? 56 : 80, fontSize: 13 }}
        />
        <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center" }}>
          {err && <div className="mono" style={{ fontSize: 11, color: "var(--v-accent)" }}>{err}</div>}
          <button
            className="v-btn v-btn-ghost"
            onClick={submit}
            disabled={busy || !body.trim()}
            data-testid={`comment-submit-${dimensionKey || "general"}`}
            style={{ padding: "8px 14px", fontSize: 11 }}
          >
            {busy ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}
